import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import React, { useState, useCallback, useEffect } from 'react';
import EditorSettings from '@wix/da-ds/pkg/Icons/24x24/EditorSettings';
import cn from 'classnames';
import { ButtonProps } from '../../../../../components/menus/buttons/types';
import SidePanel from '../../../../../components/menus/SidePanel/SidePanel';
import { useForm, useController } from 'react-hook-form';
import {
  getCurrentUser,
  getExtensionOptions,
  getExternalRenderer,
  getStoredCallback,
} from '../../../../../components/utils/tiptap';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import Flex from '@wix/da-ds/pkg/Flex';
import { useTranslation } from 'react-i18next';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import ModalHeader from '@wix/da-ds/pkg/modals/dsPresets/Modal/ModalHeader';
import { ExternalCallbacks } from '../../../../../components/Editor/types';
import { PapiDeviation } from '@wix/da-papi-types';
import NumericField from '../../../../../components/lego/NumericField';
import {
  addMissingProtocol,
  validateHref,
} from '../../../../../components/utils/validateHref';
import { EXTENSION_TYPE_NAME } from '../../../constants';
import {
  DaDeviationThumbAttrs,
  DaDeviationThumbOptions,
} from '../../../deviation-thumb';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import EditorIconButton from '../../../../../components/menus/_partials/EditorIconButton';

import buttonStyles from '../../../../../components/menus/buttons/buttons.scss';
import s from './Settings.scss';

interface Inputs {
  width: number;
  height: number;
  cropping: string;
  aspectToggle: boolean;
  link: string;
}

const Settings: React.FC<ButtonProps> = ({ editor }) => {
  const { t } = useTranslation('editor');
  const attrs: DaDeviationThumbAttrs = editor.getAttributes(
    EXTENSION_TYPE_NAME
  ) as DaDeviationThumbAttrs;
  const options = getExtensionOptions<DaDeviationThumbOptions>(
    editor,
    EXTENSION_TYPE_NAME
  );
  const Preview = getExternalRenderer(editor, 'inline');
  const deviationMediaService = getExternalRenderer(editor, 'mediaService');
  const fv = deviationMediaService.getLargestFullview(attrs.deviation);
  const ratio = (fv?.height ?? 0) / (fv?.width ?? 0);

  let maxWidth = options.maxWidth;
  let maxHeight = options.maxHeight;
  if (deviationMediaService.getDeviationType(attrs.deviation) === 'image') {
    const thumbs = deviationMediaService.getThumbSet(attrs.deviation, {
      thumbTypes: ['375W', '414W', '750W', '828W'],
    });
    const largest = thumbs[thumbs.length - 1];
    if (largest) {
      maxWidth = largest.width;
      maxHeight = largest.height;
    }
  }
  const closeSettings = () => {
    setShowSettings(false);
  };

  const [showSettings, setShowSettings] = useState(false);
  const { control, errors, trigger, setValue } = useForm<Inputs>();
  const { field: widthRange } = useController({
    name: 'width',
    control,
    defaultValue: attrs.width ?? options.defaultWidth,
  });

  const { field: heightRange } = useController({
    name: 'height',
    control,
    defaultValue: attrs.height ?? options.defaultWidth * ratio,
  });

  const { field: aspectToggle } = useController({
    name: 'aspectToggle',
    control,
    defaultValue: `${attrs.aspect}` === 'false' ? false : true,
  });

  const { field: croppingSelect } = useController({
    name: 'cropping',
    control,
    defaultValue: 'fill',
  });

  const { field: link } = useController({
    name: 'link',
    control,
    defaultValue: attrs.link ?? '',
    rules: {
      validate: value => {
        return value.length === 0 || validateHref(addMissingProtocol(value));
      },
    },
  });

  const onWidthChange = useCallback(
    e => {
      const handleWidthChange = ex => {
        if (ex) {
          editor
            .chain()
            .daDeviationThumbUpdateAttrs(
              aspectToggle.value === true
                ? {
                    width: ex,
                    height: null,
                  }
                : {
                    width: ex,
                  }
            )
            .run();
        }
      };

      if (aspectToggle.value === true) {
        heightRange.onChange(
          Math.min(parseInt(e) * ratio, maxHeight ?? Infinity)
        );
      }

      widthRange.onChange(e);
      handleWidthChange(e);
    },
    [aspectToggle.value, editor, heightRange, maxHeight, ratio, widthRange]
  );

  const onHeightChange = useCallback(
    e => {
      if (aspectToggle.value === true) {
        return;
      }
      const handleHeightChange = ex => {
        if (ex) {
          editor
            .chain()
            .daDeviationThumbUpdateAttrs({
              height: ex,
              width: attrs.width ?? options.defaultWidth,
            })
            .run();
        }
      };

      heightRange.onChange(e);
      handleHeightChange(e);
    },
    [aspectToggle.value, attrs.width, editor, heightRange, options.defaultWidth]
  );

  const onLinkChange = useCallback(
    e => {
      link.onChange(e);
      trigger('link');
      if (e) {
        const href = e.target.value.trim();
        if (href.length === 0 || validateHref(addMissingProtocol(href))) {
          editor
            .chain()
            .daDeviationThumbUpdateAttrs({
              link: href === '' ? null : addMissingProtocol(href),
            })
            .run();
        }
      }
    },
    [editor, link, trigger]
  );

  const onAspectSelect = useCallback(
    e => {
      trigger('aspectToggle');
      aspectToggle.onChange(e);
      editor
        .chain()
        .daDeviationThumbUpdateAttrs({
          height: attrs.height ?? heightRange.value,
          width: attrs.width ?? options.defaultWidth,
        })
        .run();
      editor.chain().daDeviationThumbUpdateAttrs({ aspect: e }).run();
    },
    [
      aspectToggle,
      attrs.height,
      attrs.width,
      editor,
      heightRange.value,
      options.defaultWidth,
      trigger,
    ]
  );

  const onCropSelect = useCallback(
    e => {
      trigger('cropping');
      croppingSelect.onChange(e);
      editor
        .chain()
        .daDeviationThumbUpdateAttrs({ cropping: e.target.value })
        .run();
    },
    [croppingSelect, editor, trigger]
  );

  const replaceDeviation = useCallback(() => {
    const onSelectMedia = getStoredCallback<ExternalCallbacks['onSelectMedia']>(
      editor,
      'onSelectMedia'
    );
    onSelectMedia &&
      onSelectMedia((deviations: PapiDeviation[]) => {
        if (deviations.length > 0) {
          editor.chain().daDeviationThumbReplace(deviations[0]).run();
        }
      }, {});
  }, [editor]);

  const currentUser = getCurrentUser(editor);
  const allowLink = attrs?.deviation?.author?.userId === currentUser?.userId;

  useEffect(() => {
    setValue('width', attrs.width ?? options.defaultWidth);
    !aspectToggle.value &&
      setValue('height', attrs.height ?? options.defaultWidth * ratio);
    aspectToggle.value &&
      setValue('height', attrs.height ?? widthRange.value * ratio);
    setValue('aspectToggle', attrs.aspect ?? 1);
    setValue('cropping', attrs.cropping ?? 'fill');
    setValue('link', attrs.link ?? undefined);
  }, [
    aspectToggle.value,
    attrs.aspect,
    attrs.cropping,
    attrs.height,
    attrs.link,
    attrs.width,
    options.defaultWidth,
    ratio,
    setValue,
    widthRange.value,
  ]);

  const devType = deviationMediaService.getDeviationType(attrs.deviation);

  return (
    <>
      {showSettings && (
        <SidePanel
          editor={editor}
          onRequestClose={closeSettings}
          header={<ModalHeader heading={t('ext.deviation.settings.title')} />}
          footer={
            <SolidButton
              size="medium"
              variant="approval-green"
              onClick={closeSettings}
            >
              {t('ext.deviation.settings.btn.done')}
            </SolidButton>
          }
        >
          <Flex direction="column" gap={8}>
            <div className={s['preview']}>
              <Preview
                deviation={attrs.deviation}
                width={150}
                height={devType === 'image' ? undefined : 150}
              />
              <div className={s['replace-refresh-btn-wrapper']}>
                <IconButton
                  className={cn(s['btn'])}
                  size={'small'}
                  icon={EditIcon}
                  tooltipOffset={[0, 8]}
                  aria-label={t('ext.deviation.settings.replace')}
                  tooltipText={t('ext.deviation.settings.replace')}
                  onClick={replaceDeviation}
                />
              </div>
            </div>
            <fieldset className={s['size']}>
              <legend>{t('ext.deviation.settings.size.legend')}</legend>
              <div className={s['width-range']}>
                <NumericField
                  label={t('ext.gallery.settings.container.width')}
                  {...widthRange}
                  onChange={onWidthChange}
                  maxValue={maxWidth ?? options.defaultWidth}
                  minValue={10}
                  sliderStep={1}
                />
                <NumericField
                  label={'height'}
                  {...heightRange}
                  onChange={onHeightChange}
                  maxValue={maxHeight}
                  minValue={10}
                  sliderStep={1}
                  disabled={aspectToggle.value}
                />
                <BooleanInput
                  {...aspectToggle}
                  id="aspectToggle"
                  label={t('editor.deviationThumb.settings.maintainAspect')}
                  onChange={onAspectSelect}
                />
                <SelectField
                  {...croppingSelect}
                  items={[
                    { label: 'fill', value: 'fill' },
                    { label: 'contain', value: 'contain' },
                    { label: 'cover', value: 'cover' },
                  ]}
                  onChange={onCropSelect}
                  label={t('editor.deviationThumb.settings.cropping')}
                  id="cropping"
                  withBlankOption={false}
                ></SelectField>
              </div>
            </fieldset>
            {options.canLink && allowLink && (
              <TextInput
                id="link"
                label={t('ext.deviation.settings.link.label')}
                placeholder={t('ext.deviation.settings.link.placeholder')}
                {...link}
                onChange={onLinkChange}
                validationResult={errors.link ? 'error' : 'success'}
                details={errors.link ? t('editor.deviationThumb.settings.invalidLink') : undefined}
                onFocus={e => {
                  setTimeout(() => {
                    e.target.scrollIntoView();
                  }, 300);
                }}
              />
            )}
          </Flex>
        </SidePanel>
      )}
      <EditorIconButton
        featureId="node:deviation:settings"
        icon={EditorSettings}
        onClick={() => {
          setShowSettings(true);
        }}
        className={cn(buttonStyles['button'])}
        tooltipHoverDelay={2000}
        tooltipText={t('ext.deviation.toolbar.settings')}
        aria-label={t('ext.deviation.toolbar.settings')}
      />
    </>
  );
};
Settings.displayName = 'Settings';

export default Settings;
